import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
  getCurriculums,
  getCurriculumsForHomePage,
  getSearchOptionsForHomePage,
  getSubjectMapDataBySubjectCode,
} from './CurriculumApi';
import { ISelectedOption } from '../../constants/SelectOptions';

export interface ISubject {
  code: string;
  name: string;
}

export interface IGrade {
  name: string;
  subjects: Array<ISubject>;
  subjectLevels: Array<{ name: string }>;
}

export interface ICurriculum {
  _id: string;
  name: string;
  grades: Array<IGrade>;
  createdAt: number;
  nameV2?: string;
  updateAt?: number;
}

export interface ISubjectMapDataForSubjectCode {
  _id: string;
  subject: string;
  curriculum: string;
  grade: string;
  subjectLevels: Array<{ name: string }>;
}

interface ISubTopicForHome {
  _id: string;
  name: string;
  topic: string;
}

export interface ISubjectForHome {
  name: string;
  grade: string;
  subjectCode: string;
  subjectLevel: string;
  subTopic: ISubTopicForHome;
  subjectLevelCount: number;
  subTopicsCount?: number;
}

export interface ICurriculumForHome {
  name: string;
  nameV2?: string;
  subjects: Array<ISubjectForHome>;
}

export interface ISubTopicForSearch {
  _id: string;
  name: string;
  sortingOrder?: string;
  index?: number;
}

export interface ITopicForSearch {
  _id: string;
  name: string;
  papers: string;
  subject: string;
  subjectCode: string;
  subjectLevel: string;
  curriculum: string;
  grade: string;
  sortingOrder?: number;
  subTopics: ISubTopicForSearch[];
  index?: number;
  subTopicId?: string;
  subTopicName?: string;
}

export interface ISubjectForSearch {
  subject: string;
  subjectCode: string;
  topics: ITopicForSearch[];
  subTopicId?: string;
  subTopicName?: string;
  curriculum?: string;
  subjectLevel?: string;
}

export interface ISearchOption {
  curriculum: string;
  subjects: ISubjectForSearch[];
}

export interface ICurriculumState {
  status: 'idle' | 'loading' | 'rejected';
  subjectMapDataForSubjectCode?: ISubjectMapDataForSubjectCode;
  list: ICurriculum[];
  curriculumsForHomePage: Array<ICurriculumForHome>;
  selectedValues: {
    curriculum: ISelectedOption | undefined;
    grade: ISelectedOption | undefined;
    subject: ISelectedOption | undefined;
    subjectLevel: ISelectedOption | undefined;
    topic: ISelectedOption | undefined;
    subTopic: ISelectedOption | undefined;
  };
  searchStatus: 'idle' | 'loading' | 'rejected';
  searchOptions: Array<ISearchOption>;
}

const initialState: ICurriculumState = {
  status: 'idle',
  list: [],
  curriculumsForHomePage: [],
  selectedValues: {
    curriculum: undefined,
    grade: undefined,
    subject: undefined,
    subjectLevel: undefined,
    topic: undefined,
    subTopic: undefined,
  },
  searchStatus: 'idle',
  searchOptions: [],
};

export const CurriculumSlice = createSlice({
  name: 'curriculum',
  initialState,
  reducers: {
    setSelectedValues: (state, action) => {
      state.selectedValues = { ...state.selectedValues, ...action.payload };
    },
    resetSelectedValues: (state) => {
      state.selectedValues = initialState.selectedValues;
    },
  },
  extraReducers: (builder) => {
    // List
    builder
      // Get curriculums
      .addCase(getCurriculums.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getCurriculums.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload;
      })
      .addCase(getCurriculums.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get curriculums for home page
      .addCase(getCurriculumsForHomePage.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getCurriculumsForHomePage.fulfilled, (state, action) => {
        state.status = 'idle';
        state.curriculumsForHomePage = action.payload || [];
      })
      .addCase(getCurriculumsForHomePage.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get subject map data for the subject code
      .addCase(getSubjectMapDataBySubjectCode.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getSubjectMapDataBySubjectCode.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subjectMapDataForSubjectCode = action.payload;
      })
      .addCase(getSubjectMapDataBySubjectCode.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get search options data for home
      .addCase(getSearchOptionsForHomePage.pending, (state) => {
        state.searchStatus = 'loading';
      })
      .addCase(getSearchOptionsForHomePage.fulfilled, (state, action) => {
        state.searchStatus = 'idle';
        state.searchOptions = action.payload || [];
      })
      .addCase(getSearchOptionsForHomePage.rejected, (state) => {
        state.searchStatus = 'rejected';
      });
  },
});

export const {
  setSelectedValues,
  resetSelectedValues,
} = CurriculumSlice.actions;

export const getCurriculumState = (state: RootState): RootState['curriculum'] =>
  state.curriculum;

export * from './CurriculumApi';
export default CurriculumSlice.reducer;
